import { useEffect, useState } from "react";
import {
  openSalesInvoices,
  SalesSummaryService,
} from "../../services/SalesSummaryService";
import type {
  SalesSummary,
} from "../../sales/salesSummaryPolicy";
import { DashboardCard } from "./DashboardCard";
import {
  currentBackgroundReadScope,
  forgetBackgroundRead,
  peekBackgroundRead,
  rememberBackgroundRead,
} from "../../services/BackgroundReadCache";

const SALES_SUMMARY_READ = "home-sales-summary";

function cachedSummary(workspaceId: string) {
  const cached = peekBackgroundRead<SalesSummary>(currentBackgroundReadScope(workspaceId), SALES_SUMMARY_READ);
  return cached ? { workspaceId, summary: cached.data } : null;
}
export function HomeSalesSummary(
  { workspaceId, bare, embedded }: { workspaceId: string; bare?: boolean; embedded?: boolean },
) {
  const [data, setData] = useState<
    { workspaceId: string; summary: SalesSummary } | null
  >(() => cachedSummary(workspaceId));
  useEffect(() => {
    let active = true;
    const scope = currentBackgroundReadScope(workspaceId);
    setData((previous) => previous?.workspaceId === workspaceId ? previous : cachedSummary(workspaceId));
    let version = 0;
    let unsubscribe: (() => void) | undefined;
    let invoiceSubscription = false;
    const load = async () => {
      const requestVersion = ++version;
      try {
        const result = await SalesSummaryService.get(workspaceId);
        if (active && version === requestVersion) {
          if (result.allowed && !invoiceSubscription) {
            unsubscribe?.();
            invoiceSubscription = true;
            unsubscribe = SalesSummaryService.subscribe(workspaceId, () => { void load(); });
          }
          if (scope) {
            if (result.allowed) rememberBackgroundRead(scope, SALES_SUMMARY_READ, result.summary);
            else forgetBackgroundRead(scope, SALES_SUMMARY_READ);
          }
          setData((previous) => {
            if (!result.allowed) return null;
            if (previous?.workspaceId === workspaceId &&
              JSON.stringify(previous.summary) === JSON.stringify(result.summary)) return previous;
            return { workspaceId, summary: result.summary };
          });
        }
      } catch {
        if (active && version === requestVersion) {
          if (scope) forgetBackgroundRead(scope, SALES_SUMMARY_READ);
          setData(null);
        }
      }
    };
    unsubscribe = SalesSummaryService.subscribe(workspaceId, () => { void load(); }, false);
    void load();
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [workspaceId]);
  // The backend decision is the authority. The last allowed figures draw at
  // once for the same actor and workspace; a denial or failure drops them, and
  // nothing is rendered for a denied membership or another workspace.
  if (!data || data.workspaceId !== workspaceId) return null;
  const { summary } = data;
  const invoiceMetrics: ChartMetric[] = summary.ageing ? [
    { label: "Current", filter: "unpaid", metric: summary.ageing.current, color: "var(--brand)" },
    { label: "1–30 days", filter: "overdue", metric: summary.ageing.days1To30, color: "var(--status-warning)" },
    { label: "31–60 days", filter: "overdue", metric: summary.ageing.days31To60, color: "var(--status-danger)" },
    { label: "61+ days", filter: "overdue", metric: summary.ageing.days61Plus, color: "var(--status-danger)" },
    ...(summary.ageing.unknown.count ? [{ label: "No due date", filter: "unpaid" as const, metric: summary.ageing.unknown, color: "var(--muted-foreground)" }] : []),
  ] : [
    { label: "Awaiting payment", filter: "unpaid", metric: summary.moneyDue, color: "var(--brand)" },
    { label: "Overdue", filter: "overdue", metric: summary.overdue, color: "var(--status-danger)" },
    { label: "Drafts", filter: "draft", metric: summary.drafts, color: "var(--status-warning)" },
  ];
  const hasBills = summary.billsDue && summary.billsOverdue;
  return (
    <div className={embedded ? "contents" : `grid gap-6 ${hasBills ? "@[900px]/dash:grid-cols-2" : ""}`}>
      <DashboardCard className={embedded ? "min-w-0" : undefined} title={summary.ageing ? "Customer invoice ageing" : "Customer invoices"} bare={bare}
        footer={<p className="text-xs text-muted-foreground">Recorded invoices · net of CIS and payments</p>}>
        <FinanceChart metrics={invoiceMetrics} noun="invoices" empty="No customer invoices yet" />
      </DashboardCard>
      {hasBills && <DashboardCard className={embedded ? "min-w-0" : undefined} title="Supplier bills" bare={bare}
        footer={<p className="text-xs text-muted-foreground">Recorded bills · overdue is included in awaiting payment</p>}>
        <FinanceChart metrics={[
          { label: "Awaiting payment", metric: summary.billsDue!, color: "var(--brand)" },
          { label: "Overdue", metric: summary.billsOverdue!, color: "var(--status-danger)" },
        ]} noun="bills" empty="No supplier bills recorded yet" />
      </DashboardCard>}
    </div>
  );
}

type ChartMetric = {
  label: string;
  filter?: "unpaid" | "overdue" | "draft";
  metric: SalesSummary["moneyDue"];
  color: string;
};

function FinanceChart({ metrics, noun, empty }: { metrics: ChartMetric[]; noun: string; empty: string }) {
  const currencies = [...new Set(metrics.flatMap(({ metric }) => Object.keys(metric.amounts)))].sort();
  const navigate = (filter: ChartMetric["filter"]) => {
    if (filter) openSalesInvoices(filter);
    else window.dispatchEvent(new CustomEvent("app:navigate", { detail: { view: "purchases" } }));
  };
  return <div className="grid gap-5 pt-5 pb-2 min-h-52">
    {(currencies.length ? currencies : [null]).map((currency) => {
      const max = Math.max(1, ...metrics.map(({ metric }) => currency ? Math.abs(metric.amounts[currency] ?? 0) : 0));
      const axis = (value: number) => currency
        ? new Intl.NumberFormat("en-GB", { style: "currency", currency, notation: "compact", minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(value)
        : String(value);
      const columns = { gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` };
      return <div key={currency ?? "empty"}>
        <div className="relative h-40 ml-14 border-b border-border/70">
          {[0.5, 1].map((fraction) => <div key={fraction} className="absolute inset-x-0 border-t border-border/40" style={{ bottom: `${fraction * 100}%` }}>
            <span className="absolute right-full pr-2 -translate-y-1/2 text-[10px] text-muted-foreground">{currency ? axis(max * fraction) : "—"}</span>
          </div>)}
          <span className="absolute right-full bottom-0 pr-2 text-[10px] text-muted-foreground">0</span>
          <div className="absolute inset-0 grid gap-3 items-end" style={columns}>
            {metrics.map(({ label, filter, metric, color }) => {
              const amount = currency ? metric.amounts[currency] ?? 0 : 0;
              return <button key={label} onClick={() => navigate(filter)}
                className="relative h-full flex items-end justify-center rounded-sm hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`${label}: ${currency ? axis(amount) : "0"}, ${metric.count} ${noun}`}>
                <div className="w-16 max-w-[65%] rounded-t-sm" style={{ height: `${Math.abs(amount) / max * 100}%`, backgroundColor: color }} />
              </button>;
            })}
          </div>
          {!currency && <p className="absolute inset-x-0 top-1/2 text-center text-xs text-muted-foreground pointer-events-none">{empty}</p>}
        </div>
        <div className="grid gap-3 ml-14 mt-3" style={columns}>
          {metrics.map(({ label, filter, metric }) => <button key={label} onClick={() => navigate(filter)} className="text-center rounded-md hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-ring p-1 min-w-0">
            <p className="text-xs text-muted-foreground">{label}</p>
            {currency && <p className="text-sm font-semibold tabular-nums mt-1">{new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(metric.amounts[currency] ?? 0)}</p>}
            <p className="text-[11px] text-muted-foreground mt-1">{metric.count} {metric.count === 1 ? noun.slice(0, -1) : noun}</p>
          </button>)}
        </div>
      </div>;
    })}
  </div>;
}
